import React, {JSX} from 'react';
import styled, {IStyledComponent} from 'styled-components/native';
import {useTheme} from 'styled-components';
import TextGeneric from '../../textGeneric';
import {Theme} from '../../../theme/lightTheme';
import {RadioItemProps} from '../../../interfaces/components/radioPicker';
import MyPressable from '../../myPressable';
import Animated, {FadeIn} from 'react-native-reanimated';
import Icon from '../../icon';
import {openQuestionModal} from '../../../connected-components/questionModal/actions';

const Container = styled(MyPressable)`
  width: 100%;
  align-items: center;
  padding: 2.5% 2%;
  border-radius: 2px;
  flex: 1;
`;
const BorderContainer: IStyledComponent<'native', any> = styled.View`
  align-items: center;
  justify-content: center;
  border-width: 2px;
  width: ${({theme}) => theme.text.s55}px;
  aspect-ratio: 1;
  border-radius: ${({theme}) => theme.text.s55}px;
  border-color: ${({theme, selected}) =>
    selected ? theme.primary : theme.textInput.border};
`;
const InnerBorder: IStyledComponent<'native', any> = styled.View`
  width: 50%;
  aspect-ratio: 1;
  border-radius: ${({theme}) => theme.text.s55}px;
  background-color: ${({theme}) => theme.primary};
`;
const Label = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.text.dark};
  padding-left: 1%;
  flex: 1;
`;
const InfoIconContainer = styled.TouchableOpacity`
  margin-left: auto;
  padding-left: 5%;
`;

/**
 * The `RadioItem` component renders a customizable radio button item,
 * suitable for inclusion in forms or interfaces where a single selection from
 * a group is required. It supports customizable layouts, selection and disabled
 * states, and integrates with a theme for consistent styling across an application.
 *
 * @component
 *
 * @param {RadioItemProps} props - The properties passed to the RadioItem component.
 * @param {Object} props.containerStyle - Custom styles applied to the radio item's container.
 * @param {string | JSX.Element} props.description - Optional description or additional information about the radio item, displayed in a tooltip.
 * @param {boolean} props.disabled - If true, the radio item is rendered in a disabled state, preventing interaction.
 * @param {string} props.id - A unique identifier for the radio item, used in the `onPress` callback to identify the selected item.
 * @param {string | JSX.Element} props.label - The text or element displayed as the radio item's label.
 * @param {Object} props.labelStyle - Custom styles applied to the label component of the radio item.
 * @param {'row' | 'column'} props.layout - The layout orientation of the radio item, affecting its visual arrangement.
 * @param {Function} props.onPress - Callback function invoked when the radio item is pressed, receiving the radio item's `id` as an argument.
 * @param {boolean} props.selected - If true, visually indicates that the radio item is currently selected.
 *
 * @returns {JSX.Element} A fully interactive radio item component, rendered with custom styles and behaviors as specified by the props.
 */
const RadioItem: React.FC<RadioItemProps> = ({
  containerStyle = {},
  description,
  disabled = false,
  id,
  label,
  labelStyle,
  layout = 'row',
  onPress,
  selected = false,
}: RadioItemProps): JSX.Element => {
  const theme = useTheme() as Theme;

  let orientation: any = {flexDirection: 'row'};
  let margin: any = {marginLeft: 10};

  if (layout === 'column') {
    orientation = {alignItems: 'center'};
    margin = {marginTop: 10};
  }

  function handlePress() {
    if (onPress) {
      onPress(id);
    }
  }

  const labelComp = React.isValidElement(label) ? (
    label
  ) : label ? (
    <Label style={[margin, labelStyle]}>{label}</Label>
  ) : null;
  return (
    <Animated.View entering={FadeIn} style={{flex: 1, width: '100%'}}>
      <Container
        accessibilityHint={
          typeof description === 'string' ? description : undefined
        }
        accessibilityLabel={typeof label === 'string' ? label : undefined}
        accessibilityRole="radio"
        accessibilityState={{checked: selected, disabled}}
        disabled={disabled}
        onPress={handlePress}
        style={[orientation, {opacity: disabled ? 0.2 : 1}]}
        borderRadius={4}
        containerStyle={[
          {
            width: '100%',
            backgroundColor: selected ? theme.primary + 22 : 'transparent',
            borderRadius: 4,
            borderWidth: 2,
            borderColor: selected ? theme.primary : 'transparent',
          },
          containerStyle,
        ]}>
        <BorderContainer selected={selected}>
          {selected && <InnerBorder />}
        </BorderContainer>
        {labelComp}
        {!!description && (
          <InfoIconContainer
            onPress={() =>
              openQuestionModal({
                title: 'Info',
                message: description || '',
                type: 'info',
              })
            }>
            <Icon name={'info'} color={theme.primary} size={theme.text.s5} />
          </InfoIconContainer>
        )}
      </Container>
    </Animated.View>
  );
};

export default RadioItem;
